import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Award, Clock, ChevronRight, Shield, CheckCircle, 
  GraduationCap, Scale, Users, FileCheck, ArrowRight, Star
} from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getCourseCatalog } from '@/services/api';

const MainLandingPage = () => {
  const [courses, setCourses] = useState([]);
  const { isAuthenticated, hasPaid, isAdmin, paidCourses } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        const res = await getCourseCatalog();
        setCourses(res.data);
      } catch {
        // Fallback data
        setCourses([]);
      }
    };
    fetchCatalog();
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated && isAdmin) navigate('/admin');
    else document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleEnrollCourse = (courseSlug) => {
    if (!isAuthenticated) {
      navigate(`/register?course=${courseSlug}`);
      return;
    }
    if (courseSlug === 'bail-enforcement') {
      if (paidCourses.includes('bail-enforcement')) {
        navigate('/dashboard');
      } else {
        navigate('/enroll?course=bail-enforcement');
      }
    } else if (courseSlug === 'pre-licensing' || courseSlug === 'bail-bond-prelicensing') {
      if (paidCourses.includes('pre-licensing')) {
        navigate('/pre-licensing/dashboard');
      } else {
        navigate('/enroll?course=pre-licensing');
      }
    }
  };

  const activeCourse = courses.find(c => c.status === 'active');
  const comingSoonCourses = courses.filter(c => c.status === 'coming_soon');

  return (
    <div className="min-h-screen bg-white">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge className="w-9 h-9" />
            <div className="leading-tight">
              <div className="font-black text-lg tracking-tight text-gray-900" style={{ fontFamily: 'Barlow Condensed' }}>
                COLORADO BEA
              </div>
              <div className="text-[11px] text-gray-500 font-medium tracking-wide uppercase">
                Bail Enforcement Agency LLC
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="text-gray-600 hover:text-gray-900 hidden md:inline-flex"
              onClick={() => document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-courses-link"
            >
              Courses
            </Button>
            <Button
              variant="ghost"
              className="text-gray-600 hover:text-gray-900 hidden md:inline-flex"
              onClick={() => document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="nav-about-link"
            >
              About
            </Button>
            {isAuthenticated ? (
              <Button
                onClick={() => {
                  if (isAdmin) navigate('/admin');
                  else document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-5"
                data-testid="nav-dashboard-btn"
              >
                {isAdmin ? 'Admin Portal' : 'My Courses'}
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="text-gray-700 font-medium"
                  data-testid="nav-login-btn"
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-5"
                  data-testid="nav-register-btn"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-20 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1740367217559-de1dcaafafe3?crop=entropy&cs=srgb&fm=jpg&q=85')`,
            filter: 'brightness(0.2)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-blue-900/70 via-blue-900/50 to-gray-900/90" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full mb-6">
              <Star className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300 text-sm font-semibold">Colorado's Premier Bail Industry Training</span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              Professional Certification
              <span className="block text-amber-400">For Colorado Bail Professionals</span>
            </h1>

            <p className="text-lg text-gray-300 mb-8 max-w-2xl leading-relaxed">
              Online continuing education and pre-licensing courses designed for bail bond agents 
              and enforcement professionals. DOI-compliant training built by a 28-year industry veteran.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Button
                onClick={handleGetStarted}
                className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold text-lg px-8 py-3 h-auto shadow-lg shadow-amber-500/25"
                data-testid="hero-get-started-btn"
              >
                Browse Courses <ChevronRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8 py-3 h-auto"
                onClick={() => document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-view-courses-btn"
              >
                View Catalog
              </Button>
            </div>

            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>DOI Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Self-Paced Online</span>
              </div>
              <div className="flex items-center gap-2 text-gray-300">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Certificate of Completion</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-gray-50 border-y border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-black text-blue-700" style={{ fontFamily: 'Barlow Condensed' }}>44+</div>
              <div className="text-sm text-gray-500 font-medium">Total Credit Hours</div>
            </div>
            <div>
              <div className="text-3xl font-black text-blue-700" style={{ fontFamily: 'Barlow Condensed' }}>2</div>
              <div className="text-sm text-gray-500 font-medium">Professional Courses</div>
            </div>
            <div>
              <div className="text-3xl font-black text-blue-700" style={{ fontFamily: 'Barlow Condensed' }}>28</div>
              <div className="text-sm text-gray-500 font-medium">Years Industry Experience</div>
            </div>
            <div>
              <div className="text-3xl font-black text-blue-700" style={{ fontFamily: 'Barlow Condensed' }}>100%</div>
              <div className="text-sm text-gray-500 font-medium">Online & Self-Paced</div>
            </div>
          </div>
        </div>
      </section>

      {/* Course Catalog */}
      <section id="courses" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-blue-700 font-semibold text-sm uppercase tracking-wider">Course Catalog</span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-2 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              PROFESSIONAL TRAINING COURSES
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Choose from our growing catalog of Colorado bail industry courses. 
              Each course exceeds state minimum requirements and includes full DOI audit compliance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Bail Enforcement Course - Active */}
            {activeCourse && (
              <div className="bg-white border-2 border-blue-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="course-card-enforcement">
                <div className="bg-gradient-to-r from-blue-700 to-blue-900 p-6 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded-full uppercase tracking-wide">
                      Enrolling Now
                    </span>
                    <Shield className="w-8 h-8 text-blue-300" />
                  </div>
                  <h3 className="text-2xl font-black mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
                    {activeCourse.short_title}
                  </h3>
                  <p className="text-blue-200 text-sm">{activeCourse.subtitle}</p>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-6 mb-5 text-sm">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <BookOpen className="w-4 h-4 text-blue-600" />
                      <span>{activeCourse.total_chapters} Chapters</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Clock className="w-4 h-4 text-blue-600" />
                      <span>{activeCourse.total_hours} Hours</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Award className="w-4 h-4 text-blue-600" />
                      <span>Certificate</span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                    {activeCourse.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {activeCourse.features.slice(0, 5).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <span className="text-3xl font-black text-gray-900">${activeCourse.price}</span>
                      <span className="text-gray-400 text-sm ml-1">per student</span>
                    </div>
                    <Button
                      onClick={() => handleEnrollCourse('bail-enforcement')}
                      className="bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6"
                      data-testid="enroll-enforcement-btn"
                    >
                      Enroll Now <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Pre-Licensing Course - Now Active */}
            {courses.filter(c => c.slug === 'bail-bond-prelicensing').map(course => (
              <div key={course.id} className="bg-white border-2 border-amber-200 rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow" data-testid="course-card-prelicensing">
                <div className="bg-gradient-to-r from-amber-700 to-amber-900 p-6 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 text-xs font-bold rounded-full uppercase tracking-wide">
                      Now Available
                    </span>
                    <GraduationCap className="w-8 h-8 text-amber-300" />
                  </div>
                  <h3 className="text-2xl font-black mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
                    {course.short_title}
                  </h3>
                  <p className="text-amber-200 text-sm">{course.subtitle}</p>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-6 mb-5 text-sm">
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <BookOpen className="w-4 h-4 text-amber-600" />
                      <span>{course.total_chapters} Chapters</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Clock className="w-4 h-4 text-amber-600" />
                      <span>{course.total_hours} Hours</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-gray-600">
                      <Award className="w-4 h-4 text-amber-600" />
                      <span>Certificate</span>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                    {course.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {course.features.slice(0, 5).map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div>
                      <span className="text-3xl font-black text-gray-900">${course.price}</span>
                      <span className="text-gray-400 text-sm ml-1">per student</span>
                    </div>
                    <Button
                      onClick={() => handleEnrollCourse('pre-licensing')}
                      className="bg-amber-700 hover:bg-amber-800 text-white font-semibold px-6"
                      data-testid="enroll-prelicensing-btn"
                    >
                      Enroll Now <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Combo pricing teaser */}
          <div className="mt-12 max-w-5xl mx-auto">
            <div className="bg-gradient-to-r from-blue-50 to-amber-50 border border-blue-200 rounded-xl p-6 flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed' }}>
                  BUNDLE & SAVE
                </h3>
                <p className="text-gray-600 text-sm">
                  Enroll in both courses together and save. Combo pricing available when Pre-Licensing launches.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm text-gray-400 line-through">$890</div>
                  <div className="text-2xl font-black text-blue-700">$749</div>
                </div>
                <Button disabled className="bg-gray-200 text-gray-500 font-semibold cursor-not-allowed">
                  Coming Soon
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-blue-700 font-semibold text-sm uppercase tracking-wider">Why Colorado BEA</span>
            <h2 className="text-3xl sm:text-4xl font-black text-gray-900 mt-2 mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              BUILT BY PROFESSIONALS, FOR PROFESSIONALS
            </h2>
            <p className="text-gray-500 max-w-2xl mx-auto">
              Our courses are developed by experienced Colorado bail industry professionals who understand 
              both the law and the real-world demands of the profession.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-5">
                <Scale className="w-6 h-6 text-blue-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>DOI COMPLIANT</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Full audit trail tracking, time-on-task verification, and documentation 
                ready for Colorado Division of Insurance review. Exceeds all POST E-212 requirements.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-5">
                <FileCheck className="w-6 h-6 text-amber-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>CURRENT LAW</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Updated with HB 23-1293 and all current Colorado statutes including C.R.S. 16-4-114 
                forfeiture timelines. Not outdated DVDs from years ago.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-5">
                <Users className="w-6 h-6 text-green-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>REAL CASE STUDIES</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Learn from actual Colorado cases with body camera footage and interrogation analysis. 
                See theory applied in real-world situations.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-5">
                <BookOpen className="w-6 h-6 text-purple-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>AI NARRATION</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Every chapter features AI-powered text-to-speech narration so you can listen 
                and learn at your own pace with multiple voice options.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-5">
                <Award className="w-6 h-6 text-red-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>VERIFIABLE CERTIFICATES</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Earn a verifiable Certificate of Completion with unique tracking number. 
                PDF certificates with official badge, admin-approved.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-5">
                <Clock className="w-6 h-6 text-blue-700" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed' }}>SELF-PACED</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Complete the course at your own speed with progress tracking and resume capability. 
                Access your course 24/7 from any device.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-800 to-blue-950 rounded-2xl p-8 md:p-12 text-white">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-24 h-24 bg-amber-500/20 rounded-full flex items-center justify-center shrink-0">
                <Badge className="w-14 h-14" />
              </div>
              <div>
                <span className="text-amber-400 text-sm font-semibold uppercase tracking-wider">Course Instructor</span>
                <h3 className="text-2xl font-black mt-1 mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  GILBERT TAFOYA
                </h3>
                <p className="text-blue-200 text-sm mb-1">License #403558</p>
                <p className="text-blue-200 text-sm mb-4">Colorado Bail Enforcement Agency LLC</p>
                <p className="text-blue-100 text-sm leading-relaxed">
                  With 28 years of experience in Colorado's bail industry, Gilbert Tafoya brings 
                  unmatched expertise to bail enforcement and bond agent education. This course 
                  was created to fill the void in quality training after Colorado deregulated 
                  bounty hunters in 2012.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            READY TO ADVANCE YOUR CAREER?
          </h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join the most comprehensive bail industry training program in Colorado. 
            Start your certification journey today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleGetStarted}
              className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold text-lg px-8 py-3 h-auto shadow-lg"
              data-testid="cta-get-started-btn"
            >
              Get Started Today
            </Button>
            <Button
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800 text-lg px-8 py-3 h-auto"
              onClick={() => navigate('/compliance')}
              data-testid="cta-compliance-btn"
            >
              View Compliance Info
            </Button>
          </div>
          <p className="text-sm text-gray-500 mt-6">
            Agency discount codes available. Contact us for group pricing.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-950 text-gray-400 py-12 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Badge className="w-7 h-7" />
                <span className="font-bold text-white tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                  COLORADO BEA
                </span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                Professional bail industry education and certification for Colorado. 
                Exceeding DOI standards since day one.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-3 uppercase tracking-wide">Courses</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => handleEnrollCourse('bail-enforcement')} className="hover:text-white transition-colors">
                    Bail Enforcement Certification
                  </button>
                </li>
                <li>
                  <button onClick={() => handleEnrollCourse('pre-licensing')} className="hover:text-white transition-colors">
                    Bail Bond Pre-Licensing
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white text-sm mb-3 uppercase tracking-wide">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <button onClick={() => navigate('/compliance')} className="hover:text-white transition-colors">
                    POST Compliance
                  </button>
                </li>
                <li>
                  <button onClick={() => navigate('/login')} className="hover:text-white transition-colors">
                    Student Login
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-600">
            Colorado Bail Enforcement Agency LLC. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLandingPage;
